import { useCallback } from 'react';
import useFetch from 'hooks/useFetch';

export default function useOpenAIFetch() {
  const [fetchedProjects, fetchProjects] = useFetch();
  const [fetchedAssistants, fetchAssistants] = useFetch();
  const [fetchedAssistantDetails, fetchAssistantDetails] = useFetch();
  const [updateAssistantFetch, updateAssistantRequest] = useFetch();
  const [createAssistantFetch, createAssistantRequest] = useFetch();
  const [deleteAssistantFetch, deleteAssistantRequest] = useFetch();
  const [cloneAssistantFetch, cloneAssistantRequest] = useFetch();
  const [fetchedVectorStores, fetchVectorStores] = useFetch();
  const [fetchedVectorStoreDetails, fetchVectorStoreDetails] = useFetch();
  const [createVectorStoreFetch, createVectorStoreRequest] = useFetch();
  const [updateVectorStoreFetch, updateVectorStoreRequest] = useFetch();
  const [deleteVectorStoreFetch, deleteVectorStoreRequest] = useFetch();
  const [uploadVectorStoreFileFetch, uploadVectorStoreFileRequest] = useFetch();
  const [fetchedFiles, fetchFiles] = useFetch();
  const [deleteFileFetch, deleteFileRequest] = useFetch();
  const [fetchedVectorStoreFiles, fetchVectorStoreFiles] = useFetch();
  const [associateVectorStoreFetch, associateVectorStoreRequest] = useFetch();
  const [removeVectorStoreFetch, removeVectorStoreRequest] = useFetch();

  // PROJECTS
  // useCallback con [] porque no depende de ningún estado externo
  const fetchOpenAIProjects = useCallback(async () => {
    if (fetchedProjects.isCalled) return;
    return await fetchProjects({
      url: 'openai/projects',
      method: 'GET',
      messageKo: 'Error obteniendo proyectos de OpenAI',
    });
  }, []);

  // ASSISTANTS
  // FIX PRINCIPAL: useCallback estabiliza la referencia de la función entre renders.
  // Sin esto, cada render crea una nueva función → el useEffect de ChatPage que la
  // tiene en dependencias se dispara infinitamente.
  const fetchOpenAIAssistants = useCallback(async (projectId) => {
    if (!projectId) return;
    return await fetchAssistants({
      url: `assistants/?project_id=${projectId}`,
      method: 'GET',
      messageKo: 'Error obteniendo asistentes de OpenAI',
    });
  }, []);

  const fetchOpenAIAssistantDetails = useCallback(async (projectId, assistantId) => {
    if (!projectId || !assistantId || assistantId === "create") {
      console.warn("⚠️ Evitando GET en assistants/create");
      return;
    }
    const res = await fetchAssistantDetails({
      url: `assistants/${assistantId}?project_id=${projectId}`,
      method: 'GET',
      messageKo: 'Error obteniendo detalles del Assistant',
    });
    if (res && res.vector_stores) {
      fetchedVectorStoreFiles.data = res.vector_stores;
    }
    return res;
  }, []);

  const updateAssistant = useCallback(async (projectId, assistantId, updatedData) => {
    if (!projectId || !assistantId) return;
    const res = await updateAssistantRequest({
      url: `assistants/${assistantId}?project_id=${projectId}`,
      method: 'POST',
      body: updatedData,
      messageKo: 'Error actualizando assistant',
    });
    await fetchOpenAIAssistantDetails(projectId, assistantId);
    return res;
  }, [fetchOpenAIAssistantDetails]);

  const createOpenAIAssistant = useCallback(async (projectId, assistantData) => {
    if (!projectId) {
      console.error('❌ No hay projectId seleccionado');
      return;
    }
    const payload = {
      project_id: projectId,
      model: assistantData.model || 'gpt-4o',
      name: assistantData.name || 'Nuevo Asistente',
      description: assistantData.description || '',
      instructions: assistantData.instructions || '',
      tools: assistantData.tools || [],
      metadata: assistantData.metadata || {},
      temperature: assistantData.temperature ?? 0.8,
      top_p: assistantData.top_p ?? 0.9,
      response_format: assistantData.response_format || 'auto'
    };
    console.log('📤 Enviando request para crear asistente:', payload);
    const res = await createAssistantRequest({
      url: `assistants/create`,
      method: 'POST',
      body: payload,
      messageKo: 'Error creando assistant',
    });
    console.log('✅ Petición enviada correctamente');
    return res;
  }, []);

  const deleteOpenAIAssistant = useCallback(async (projectId, assistantId) => {
    if (!projectId || !assistantId) {
      console.error("❌ Falta projectId o assistantId");
      return;
    }
    console.log(`🗑️ Eliminando assistant ${assistantId} del proyecto ${projectId}`);
    const res = await deleteAssistantRequest({
      url: `assistants/${assistantId}?project_id=${projectId}`,
      method: "DELETE",
      messageKo: "Error eliminando assistant",
    });
    console.log("✅ Assistant eliminado correctamente");
    await fetchOpenAIAssistants(projectId);
    return res;
  }, [fetchOpenAIAssistants]);

  const cloneOpenAIAssistant = useCallback(async (projectId, assistantId) => {
    if (!projectId || !assistantId) {
      console.error("❌ Falta projectId o assistantId");
      return;
    }
    const res = await cloneAssistantRequest({
      url: `assistants/${assistantId}/clone?project_id=${projectId}`,
      method: "POST",
      messageKo: "Error duplicando assistant",
    });
    await fetchOpenAIAssistants(projectId);
    return res;
  }, [fetchOpenAIAssistants]);

  // VECTOR STORES
  const fetchOpenAIVectorStores = useCallback(async (projectId) => {
    if (!projectId) return;
    return await fetchVectorStores({
      url: `vectorstores/list?project_id=${projectId}`,
      method: 'GET',
      messageKo: 'Error obteniendo vector stores de OpenAI',
    });
  }, []);

  const fetchOpenAIVectorStoreDetails = useCallback(async (projectId, vectorStoreId) => {
    if (!projectId || !vectorStoreId) return;
    return await fetchVectorStoreDetails({
      url: `vectorstores/${vectorStoreId}?project_id=${projectId}`,
      method: 'GET',
      messageKo: 'Error obteniendo detalles de la Vector Store',
    });
  }, []);

  const createOpenAIVectorStore = useCallback(async (projectId, vectorStoreData) => {
    if (!projectId) {
      console.error('❌ No hay projectId seleccionado');
      return;
    }
    const payload = {
      name: vectorStoreData.name,
      file_ids: vectorStoreData.file_ids || [],
      metadata: vectorStoreData.metadata || {},
      chunking_strategy: typeof vectorStoreData.chunking_strategy === "object"
        ? vectorStoreData.chunking_strategy
        : { type: "auto" },
      expires_after: vectorStoreData.expires_after || null,
    };
    console.log('📤 Enviando request para crear Vector Store:', payload);
    const res = await createVectorStoreRequest({
      url: `vectorstores/create?project_id=${projectId}`,
      method: 'POST',
      body: payload,
      messageKo: 'Error creando Vector Store',
    });
    console.log('✅ Vector Store creada correctamente');
    await fetchOpenAIVectorStores(projectId);
    return res;
  }, [fetchOpenAIVectorStores]);

  const updateOpenAIVectorStore = useCallback(async (projectId, vectorStoreId, updatedData) => {
    if (!projectId || !vectorStoreId) return;
    const res = await updateVectorStoreRequest({
      url: `vectorstores/${vectorStoreId}?project_id=${projectId}`,
      method: 'POST',
      body: updatedData,
      messageKo: 'Error actualizando la Vector Store',
    });
    await fetchOpenAIVectorStoreDetails(projectId, vectorStoreId);
    return res;
  }, [fetchOpenAIVectorStoreDetails]);

  const deleteOpenAIVectorStore = useCallback(async (projectId, vectorStoreId) => {
    if (!projectId || !vectorStoreId) {
      console.error("❌ Falta projectId o vectorStoreId");
      return;
    }
    console.log(`🗑️ Eliminando Vector Store ${vectorStoreId} del proyecto ${projectId}`);
    const res = await deleteVectorStoreRequest({
      url: `vectorstores/${vectorStoreId}?project_id=${projectId}`,
      method: "DELETE",
      messageKo: "Error eliminando Vector Store",
    });
    console.log("✅ Vector Store eliminada correctamente");
    await fetchOpenAIVectorStores(projectId);
    return res;
  }, [fetchOpenAIVectorStores]);

  const uploadFileToVectorStore = useCallback(async (projectId, file, purpose) => {
    const formData = new FormData();
    formData.append("file", file, file.name);
    formData.append("purpose", purpose);
    return await uploadVectorStoreFileRequest({
      url: `vectorstores/upload?project_id=${projectId}`,
      method: "POST",
      body: formData,
      headers: {},
      messageKo: "Error subiendo el archivo",
    });
  }, []);

  const fetchUploadedFiles = useCallback(async (projectId) => {
    if (!projectId) return;
    return await fetchFiles({
      url: `vectorstores/files?project_id=${projectId}`,
      method: "GET",
      messageKo: "Error obteniendo archivos subidos",
    });
  }, []);

  const deleteUploadedFile = useCallback(async (projectId, fileId) => {
    if (!projectId || !fileId) return;
    console.log(`🗑️ Eliminando archivo ${fileId}`);
    const res = await deleteFileRequest({
      url: `vectorstores/files/${fileId}?project_id=${projectId}`,
      method: "DELETE",
      messageKo: "Error eliminando archivo",
    });
    await fetchUploadedFiles(projectId);
    return res;
  }, [fetchUploadedFiles]);

  const fetchFilesInVectorStore = useCallback(async (projectId, vectorStoreId) => {
    if (!projectId || !vectorStoreId) return;
    return await fetchVectorStoreFiles({
      url: `vectorstores/${vectorStoreId}/files?project_id=${projectId}`,
      method: "GET",
      messageKo: "Error obteniendo archivos de la Vector Store",
    });
  }, []);

  const addFileToVectorStore = useCallback(async (projectId, vectorStoreId, fileId, chunkingStrategy = { type: "auto" }) => {
    if (!projectId || !vectorStoreId || !fileId) {
      console.error("❌ Falta projectId, vectorStoreId o fileId.");
      return;
    }
    const payload = {
      file_id: fileId,
      chunking_strategy: chunkingStrategy
    };
    console.log("📤 Enviando archivo con parámetros:", JSON.stringify(payload, null, 2));
    const res = await updateVectorStoreRequest({
      url: `vectorstores/${vectorStoreId}/files?project_id=${projectId}`,
      method: "POST",
      body: payload,
      messageKo: "Error añadiendo archivo a la Vector Store",
    });
    await fetchFilesInVectorStore(projectId, vectorStoreId);
    return res;
  }, [fetchFilesInVectorStore]);

  const removeFileFromVectorStore = useCallback(async (projectId, vectorStoreId, fileId) => {
    if (!projectId || !vectorStoreId || !fileId) return;
    const res = await deleteFileRequest({
      url: `vectorstores/${vectorStoreId}/files/${fileId}?project_id=${projectId}`,
      method: "DELETE",
      messageKo: "Error eliminando archivo de la Vector Store",
    });
    await fetchFilesInVectorStore(projectId, vectorStoreId);
    return res;
  }, [fetchFilesInVectorStore]);

  const associateVectorStoreToAssistant = useCallback(async (projectId, assistantId, vectorStoreId) => {
    if (!projectId || !assistantId || !vectorStoreId) {
      console.error("❌ Falta projectId, assistantId o vectorStoreId.");
      return;
    }
    console.log(`📤 Asociando Vector Store ${vectorStoreId} a Assistant ${assistantId}`);
    const res = await associateVectorStoreRequest({
      url: `assistants/${assistantId}/vectorstores?project_id=${projectId}`,
      method: "POST",
      body: { vector_store_id: vectorStoreId },
      messageKo: "Error asociando Vector Store al Assistant",
    });
    await fetchOpenAIAssistantDetails(projectId, assistantId);
    return res;
  }, [fetchOpenAIAssistantDetails]);

  const removeVectorStoreFromAssistant = useCallback(async (projectId, assistantId, vectorStoreId) => {
    if (!projectId || !assistantId || !vectorStoreId) {
      console.error("❌ Falta projectId, assistantId o vectorStoreId.");
      return;
    }
    console.log(`🗑️ Eliminando Vector Store ${vectorStoreId} de Assistant ${assistantId}`);
    const res = await removeVectorStoreRequest({
      url: `assistants/${assistantId}/vectorstores/${vectorStoreId}?project_id=${projectId}`,
      method: "DELETE",
      messageKo: "Error eliminando Vector Store del Assistant",
    });
    await fetchOpenAIAssistantDetails(projectId, assistantId);
    return res;
  }, [fetchOpenAIAssistantDetails]);

  return {
    // Projects
    fetchedProjects,
    fetchOpenAIProjects,
    // Assistants
    fetchedAssistants,
    fetchOpenAIAssistants,
    fetchedAssistantDetails,
    fetchOpenAIAssistantDetails,
    updateAssistant,
    createOpenAIAssistant,
    createAssistantFetch,
    deleteOpenAIAssistant,
    deleteAssistantFetch,
    cloneOpenAIAssistant,
    cloneAssistantFetch,
    // Vector Stores
    fetchedVectorStores,
    fetchOpenAIVectorStores,
    fetchedVectorStoreDetails,
    fetchOpenAIVectorStoreDetails,
    createOpenAIVectorStore,
    createVectorStoreFetch,
    updateOpenAIVectorStore,
    updateVectorStoreFetch,
    deleteOpenAIVectorStore,
    deleteVectorStoreFetch,
    uploadFileToVectorStore,
    uploadVectorStoreFileFetch,
    fetchFilesInVectorStore,
    addFileToVectorStore,
    removeFileFromVectorStore,
    fetchUploadedFiles,
    fetchedFiles,
    fetchedVectorStoreFiles,
    associateVectorStoreToAssistant,
    removeVectorStoreFromAssistant,
    associateVectorStoreFetch,
    removeVectorStoreFetch,
  };
}